import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CollectionEntryKind } from "@connect/shared";
import { useApi } from "./api";

export interface Collection {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  created_at: number;
  updated_at: number;
  entry_count: number;
  last_added_at: number | null;
  /** Days since the most recent addition (or since creation if empty). */
  quiet_days: number;
}

export interface CollectionMembership {
  id: string;
  name: string;
  slug: string;
  entry_id: string;
}

export function useCollections() {
  const api = useApi();
  return useQuery({
    queryKey: ["collections"],
    queryFn: () => api.get<{ collections: Collection[] }>("/collections"),
    staleTime: 30_000,
  });
}

/**
 * Which collections this specific entry already belongs to, plus mutations
 * to add/remove. Lookup runs once per opened menu; cache is keyed by entry.
 */
export function useEntryCollections(kind: CollectionEntryKind, entryId: string | null) {
  const api = useApi();
  const qc = useQueryClient();

  const enabled = !!entryId;
  const lookup = useQuery({
    queryKey: ["collections-lookup", kind, entryId],
    queryFn: () =>
      api.get<{ memberships: CollectionMembership[] }>(
        `/collections/entries/lookup?kind=${encodeURIComponent(kind)}&id=${encodeURIComponent(entryId!)}`,
      ),
    enabled,
    staleTime: 10_000,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["collections-lookup", kind, entryId] });
    qc.invalidateQueries({ queryKey: ["collections"] });
    qc.invalidateQueries({ queryKey: ["collection"] });
  };

  const add = useMutation({
    mutationFn: (collectionId: string) =>
      api.post(`/collections/${collectionId}/entries`, {
        entry_kind: kind,
        entry_id: entryId,
      }),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: ({ collectionId, entryId: eid }: { collectionId: string; entryId: string }) =>
      api.del(`/collections/${collectionId}/entries/${eid}`),
    onSuccess: invalidateAll,
  });

  const createAndAdd = useMutation({
    mutationFn: async (name: string) => {
      const created = await api.post<{ id: string; slug: string }>("/collections", { name });
      await api.post(`/collections/${created.id}/entries`, {
        entry_kind: kind,
        entry_id: entryId,
      });
      return created;
    },
    onSuccess: invalidateAll,
  });

  return { lookup, add, remove, createAndAdd };
}
